import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { DailyTrendChart } from '@/components/DailyTrendChart';
import { Karta } from '@/components/Karta';
import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { PasSekcji } from '@/components/PasSekcji';
import { SlupkiRankingowe, SzeregCzasowy } from '@/components/wykresy/Wykresy';
import { DateRangeFilter } from '@/components/DateRangeFilter';
import { MetricBasisChips } from '@/components/MetricBasisChips';
import { MiniMetryka, PlytaGlowna } from '@/components/PlytaGlowna';
import { PrzyciskDowodu, type Dowod } from '@/components/SzufladaDowodu';
import { ErrorState, LoadingState } from '@/components/states';
import { formatCount, formatGrosze } from '@/lib/format';
import { czyPustyZbior, wartoscDoPokazania } from '@/lib/pustyZbior';
import { oknoZrownowazone, rytmTygodnia } from '@/lib/rytmTygodnia';
import { useDateRange } from '@/lib/useDateRange';
import { useSchoolSales } from '@/lib/useAnalytics';

/**
 * SPRZEDAŻ WG SZKÓŁ — migracja do języka „SYGNAŁ" (kierunek C, R5-15-009).
 *
 * PYTANIE EKRANU: ile sprzedajemy, gdzie i jak to się zmienia.
 *
 * Ekran czyta WYŁĄCZNIE `/api/crm/analytics/school-sales` przez istniejący hak `useSchoolSales`.
 * Żadnej liczby nie ma tu wpisanej w kodzie i żadnej nie da się dopisać „dla przykładu" — gdy
 * źródło jest puste, ekran pokazuje pustkę i nazywa jej rodzaj.
 *
 * TRZY STANY, KTÓRYCH NIE WOLNO ZLAĆ W JEDEN:
 *   • PUSTE ŹRÓDŁO   — zero zamówień i zero szkół. Nie było czego sumować. Sygnał TECHNICZNY.
 *   • ZMIERZONE ZERO — szkoły są, sprzedaży nie ma. Źródło odpowiedziało. Sygnał BIZNESOWY.
 *   • WYNIK          — jest co pokazać.
 * Reguła i jej uzasadnienie mieszkają w `lib/pustyZbior.ts`, razem z testami, bo to niezmiennik
 * całego produktu, a nie szczegół tego ekranu.
 */

/** Jedno źródło prawdy o nazwie źródła — powtórzone w płycie i w każdym dowodzie. */
const ZRODLO = 'CRM /analytics/school-sales → crm_order_items, szkoła z transakcji';

/**
 * Definicja sprzedaży operacyjnej po stronie backendu (crm/backend/lib/sales.js). Wpisana tu
 * dosłownie, bo dowód ma opisywać TO, co się naprawdę wydarzyło w zapytaniu, a nie parafrazę.
 */
const DEFINICJA_SPRZEDAZY = 'transaction_type ∈ {0, 2} · status = 1 · amount_grosze < 100000';

export function SchoolSalesPage() {
  const [range] = useDateRange();
  const { data, isLoading, error } = useSchoolSales();

  /*
   * Okno bierzemy z ODPOWIEDZI, gdy tylko jest — to ono zostało policzone. Zakres z adresu jest
   * tylko żądaniem. Strzałkę składamy sami, bo formatRange() zwija okno jednodniowe do jednej
   * daty, a stan pusty musi ZAWSZE podać okres w postaci „od → do".
   */
  const oknoOd = data?.range.date_from ?? range.date_from;
  const oknoDo = data?.range.date_to ?? range.date_to;
  const okno = `${oknoOd} → ${oknoDo}`;

  const wiersze = data?.schools ?? [];
  const liczbaZamowien = data?.summary.total_orders ?? null;
  const liczbaSzkolWZasiegu = data ? wiersze.length : null;

  /*
   * ZIARNO rozstrzyga o pustce LICZBĄ WIERSZY, nie wartością. Suma z pustego zbioru wynosi 0,
   * więc `total_sales_grosze: 0` przychodzi tak samo wtedy, gdy sprzedaż naprawdę wyniosła zero,
   * jak i wtedy, gdy nie było ani jednego wiersza do zsumowania.
   */
  const ziarno = { wierszy: liczbaZamowien, bytow: liczbaSzkolWZasiegu };
  const zrodloPuste = czyPustyZbior(ziarno);
  const brakOdpowiedzi = !isLoading && !error && !data;
  const zmierzoneZero = Boolean(data) && !zrodloPuste && liczbaZamowien === 0;

  const wartoscSprzedazy = wartoscDoPokazania(data?.summary.total_sales_grosze, ziarno);
  const zamowieniaDoPokazania = wartoscDoPokazania(liczbaZamowien, ziarno);
  const szkolyDoPokazania = wartoscDoPokazania(liczbaSzkolWZasiegu, ziarno);

  /*
   * Ranking sortujemy TUTAJ i wprost po wartości okresu. Backend sortuje wiersze wg własnej
   * klasyfikacji (recent/baseline Szybkiego Alertu), a to jest inne uporządkowanie niż „kto
   * sprzedał najwięcej". Nazywamy więc kolejność jawnie, zamiast zakładać, że ta z API pasuje.
   */
  const ranking = [...wiersze].sort((a, b) => b.period_sales_grosze - a.period_sales_grosze);
  const maksWiersza = ranking.length ? ranking[0]!.period_sales_grosze : 0;
  const szkolZeSprzedaza = wiersze.filter((r) => r.period_sales_grosze > 0).length;
  const szkolBezSprzedazy = wiersze.length - szkolZeSprzedaza;
  /* period_days jest IDENTYCZNE w każdym wierszu (liczba dni kalendarzowych okna), więc jako
     kolumna nie niosło informacji. Podajemy je raz, w podpisie tabeli. */
  const dniKalendarzowe = ranking.length ? ranking[0]!.period_days : null;

  /*
   * Dowody budujemy z TYCH SAMYCH pól, które zasilają liczby na ekranie. Warstwa prezentacji nie
   * może być właścicielem liczb — inaczej opis i wartość rozjeżdżają się przy pierwszej zmianie.
   */
  const dowodWartosci: Dowod = {
    tytul: 'Wartość sprzedaży wg szkół (ceny katalogowe)',
    etykietaWartosci: 'Wartość wg cennika',
    wartosc: wartoscSprzedazy === null ? null : formatGrosze(wartoscSprzedazy),
    podstawa: { value: 'LIST_VALUE', volume: 'ORDER_COUNT', time: 'DELIVERY_DATE' },
    zrodlo: ZRODLO,
    klasyfikacja: 'SWIEZOSC_NIEUSTALONA',
    swiezosc: 'NIEUSTALONA — źródło nie raportuje momentu odczytu',
    oknoBiezace: okno,
    // Endpoint NIE zwraca okna porównawczego dla wartości okresu. Wpisanie tu jakiegokolwiek
    // zakresu sugerowałoby, że coś z czymś porównano — a nie porównano.
    oknoPorownawcze: null,
    wyliczenie:
      'Σ price_grosze po ŻYWYCH pozycjach zamówienia (is_deleted IS NOT TRUE), dla zamówień ' +
      'zdeduplikowanych po identyfikatorze; okno cięte po DACIE DOSTAWY.',
    szczegoly: [
      ['Definicja sprzedaży', DEFINICJA_SPRZEDAZY],
      ['Przypisanie do szkoły', 'szkoła z TRANSAKCJI (stan z dnia zakupu)'],
      ['Zamówienia w oknie', formatCount(zamowieniaDoPokazania)],
      ['Szkoły w zasięgu analizy', formatCount(szkolyDoPokazania)],
      ['Szkoły ze sprzedażą', data ? formatCount(szkolZeSprzedaza) : '—'],
    ],
    proba: `${formatCount(zamowieniaDoPokazania)} zamówień w ${formatCount(szkolyDoPokazania)} szkołach`,
    ograniczenia: [
      ...(zrodloPuste
        ? [
            'ŹRÓDŁO ZWRÓCIŁO PUSTY ZBIÓR: zero zamówień i zero szkół w tym oknie. To NIE jest ' +
              'zmierzone zero — nie ma czego sumować.',
          ]
        : []),
      ...(zmierzoneZero
        ? [
            'ZMIERZONE ZERO: szkoły w zasięgu analizy są, ale żadna nie ma w tym oknie ' +
              'zamówienia. Źródło odpowiedziało — to wynik, nie awaria zasilania.',
          ]
        : []),
      'Pozycje skasowane administracyjnie nie wchodzą do wartości, choć pieniądze mogły wpłynąć — ' +
        'skasowanie pozycji nie jest w tym systemie anulowaniem ani zwrotem.',
      'Guard anomalii odcina transakcje od 1000 zł wzwyż; sprzedaż opłacona taką transakcją nie ' +
        'jest tu liczona.',
      'Zasięg obejmuje także szkoły dziś zablokowane, jeśli miały obrót w oknie — inaczej historia ' +
        'znikałaby po blokadzie.',
      'Świeżość źródła nieustalona: liczby są poprawne dla momentu odczytu, ale ten moment jest nieznany.',
    ],
    czegoNieWynika:
      'To NIE jest przychód i nie wolno tej liczby tak nazwać. Ceny katalogowe są sprzed zniżki ' +
      'szkolnej, a zniżka jest różna w różnych szkołach — kwota, która wpłynęła, jest niższa i tu ' +
      'w ogóle nie jest sumowana. Nie wynika stąd też żaden trend: endpoint nie liczy okna ' +
      'porównawczego dla tej wartości, a przy nieustalonej świeżości źródła nie wiadomo nawet, ' +
      'czy dwa odczyty byłyby równie aktualne.',
  };

  const dowodZamowien: Dowod = {
    tytul: 'Liczba zamówień w oknie',
    etykietaWartosci: 'Zamówienia',
    wartosc: zamowieniaDoPokazania === null ? null : formatCount(zamowieniaDoPokazania),
    podstawa: { volume: 'ORDER_COUNT', time: 'DELIVERY_DATE' },
    zrodlo: ZRODLO,
    klasyfikacja: 'SWIEZOSC_NIEUSTALONA',
    swiezosc: 'NIEUSTALONA — źródło nie raportuje momentu odczytu',
    oknoBiezace: okno,
    oknoPorownawcze: null,
    wyliczenie: 'COUNT(DISTINCT identyfikator zamówienia) po pozycjach z okna.',
    szczegoly: [
      ['Deduplikacja', 'po zamówieniu — jedno zamówienie opłacone portfelem i przelewem liczy się RAZ'],
      ['Definicja sprzedaży', DEFINICJA_SPRZEDAZY],
    ],
    proba: `${formatCount(zamowieniaDoPokazania)} zamówień`,
    ograniczenia: [
      'Jednostką jest ZAMÓWIENIE, nie transakcja płatnicza i nie pozycja zamówienia — to trzy różne liczby.',
    ],
    czegoNieWynika:
      'Z liczby zamówień NIE wynika liczba porcji: jedno zamówienie bywa na kilka dań, a tego ' +
      'źródło na tym ziarnie nie zwraca. Nie wynika z niej też liczba uczniów — jeden kupujący ' +
      'składa w oknie wiele zamówień, więc te dwie wielkości nie są zamienne.',
  };

  const dowodPorcji: Dowod = {
    tytul: 'Liczba porcji w oknie',
    etykietaWartosci: 'Porcje',
    // Świadomie null. Ta liczba nie istnieje w tym źródle i nie wolno jej podstawić liczbą zamówień.
    wartosc: null,
    podstawa: { volume: 'PORTION_COUNT', time: 'DELIVERY_DATE' },
    zrodlo: 'brak — /analytics/school-sales nie zwraca ilości z pozycji zamówienia',
    klasyfikacja: 'BRAK_ZRODLA',
    swiezosc: 'nie dotyczy — nie ma czego datować',
    oknoBiezace: okno,
    oknoPorownawcze: null,
    wyliczenie:
      'Nie policzono. Endpoint sprzedaży wg szkół agreguje wartość i liczbę zamówień; ilości ' +
      'z pozycji (qty) nie ma w jego odpowiedzi.',
    ograniczenia: [
      'Podstawienie tu liczby zamówień byłoby zmyśleniem wyniku — zamówienie i porcja to dwie różne jednostki.',
    ],
    czegoNieWynika:
      'Brak tej liczby NIE znaczy, że porcji nie było ani że było ich tyle, co zamówień. Znaczy ' +
      'dokładnie tyle, że ten ekran ich nie zna — kto potrzebuje wolumenu w porcjach, musi sięgnąć ' +
      'po źródło, które trzyma ilości z pozycji zamówienia.',
  };

  const dowodRankingu: Dowod = {
    tytul: 'Ranking szkół wg wartości sprzedaży',
    etykietaWartosci: 'Szkoły w zasięgu analizy',
    wartosc: szkolyDoPokazania === null ? null : formatCount(szkolyDoPokazania),
    podstawa: { value: 'LIST_VALUE', volume: 'ORDER_COUNT', time: 'DELIVERY_DATE' },
    zrodlo: ZRODLO,
    klasyfikacja: 'SWIEZOSC_NIEUSTALONA',
    swiezosc: 'NIEUSTALONA — źródło nie raportuje momentu odczytu',
    oknoBiezace: okno,
    oknoPorownawcze: null,
    wyliczenie:
      'Wiersze z odpowiedzi, uporządkowane malejąco po wartości okresu (period_sales_grosze). ' +
      'Pasek w wierszu to udział wiersza w największej wartości rankingu, liczony z tej samej liczby, ' +
      'którą widać obok.',
    szczegoly: [
      ['Szkoły ze sprzedażą', data ? formatCount(szkolZeSprzedaza) : '—'],
      ['Szkoły bez sprzedaży', data ? formatCount(szkolBezSprzedazy) : '—'],
      ['Dni kalendarzowe okna', formatCount(dniKalendarzowe)],
      ['Kolejność', 'nadana na tym ekranie, nie przejęta z API'],
    ],
    proba: `${formatCount(szkolyDoPokazania)} szkół w zasięgu`,
    ograniczenia: [
      'Zasięg analizy to nie to samo co „szkoły, które sprzedały" — szkoła bez ani jednego zamówienia też tu jest.',
      '„Dni wg grafiku" to dni, w których szkoła wg kalendarza i grafiku MIAŁA sprzedawać, a nie dni, w których sprzedała.',
    ],
    czegoNieWynika:
      'Z pozycji w tym rankingu NIE wynika, która szkoła jest najbardziej dochodowa: zniżka szkolna ' +
      'jest różna w różnych szkołach, więc po zniżce kolejność bywa inna niż po cenach katalogowych. ' +
      'Nie wynika też, że szkoła bez sprzedaży przestała zamawiać — mogła nie mieć w tym oknie ani ' +
      'jednego dnia dostaw wg grafiku.',
  };

  const pokazPustke = brakOdpowiedzi || zrodloPuste || zmierzoneZero;

  /*
   * RYTM TYGODNIA liczymy z serii dziennej, którą ekran i tak już czyta — patrz lib/rytmTygodnia.
   * Makieta ma tu sekcję, produkt jej nie miał, a kontrakt nie wymagał ani jednego nowego pola:
   * dzień tygodnia jest funkcją daty punktu.
   */
  const rytm = rytmTygodnia(data?.charts?.daily_trend);
  const rytmZrownowazony = oknoZrownowazone(rytm);

  return (
    <Box>
      {/*
        NAGŁÓWEK EKRANU z makiety: tytuł i podtytuł po lewej, sterowanie widokiem dociągnięte
        do prawej krawędzi TEGO SAMEGO wiersza. Filtr stał wcześniej pod spodem jako karta na
        pełną szerokość — czytało się to jak zawartość ekranu, a nie jak jego ustawienie.
      */}
      <NaglowekEkranu
        tytul="Sprzedaż wg szkół"
        opis="Ile sprzedajemy, gdzie i jak to się zmienia — wartość w cenach katalogowych rozbita na szkoły, liczona po dniu dostawy. Każda liczba prowadzi do swojego wyprowadzenia."
        akcja={<DateRangeFilter wariant="naglowek" />}
      />

      {/* Podstawa metryki PRZY liczbach, nie w przypisie — patrz MetricBasisChips. */}
      <Box sx={{ mb: 1.5 }}>
        <MetricBasisChips basis={{ value: 'LIST_VALUE', volume: 'ORDER_COUNT', time: 'DELIVERY_DATE' }} />
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: '78ch' }}>
        Szkoła przypisana zgodnie z transakcją z dnia zakupu, nie z bieżącą szkołą ucznia. Zmiana szkoły przez ucznia
        nie przepisuje historii.
      </Typography>

      {isLoading ? <LoadingState /> : null}
      {error ? (
        <ErrorState
          error={error}
          context="Control Center czyta wyłącznie API. Gdy backend nie odpowiada albo sesja wygasła, nie pokazujemy liczb zastępczych."
        />
      ) : null}

      {/*
        BRAK DANYCH NIE USUWA UKŁADU.

        Do R5-15-014 ten ekran przy pustym źródle ZWIJAŁ SIĘ do samego komunikatu: znikała płyta,
        znikał szereg czasowy, znikał ranking. Makieta trzyma te ramy zawsze — i słusznie, bo rama
        odpowiada na pytanie „co ten ekran w ogóle pokazuje", a komunikat tylko na „dlaczego dziś
        nic tu nie ma". Ekran bez ram nie uczy niczego o produkcie i wygląda na niedokończony.

        Komunikat zostaje — przenosi się NAD ramy zamiast je zastępować. Liczb z makiety nie
        kopiujemy; prymitywy same rysują stan pusty po otrzymaniu `null`.
      */}
      {!isLoading && !error && pokazPustke ? (
        <Stack spacing={3} className="sygnal-kaskada">
          <PustkaSprzedazy
            rodzaj={zmierzoneZero ? 'zmierzone-zero' : 'puste-zrodlo'}
            okno={okno}
            szkolWZasiegu={liczbaSzkolWZasiegu}
            dowod={dowodWartosci}
          />

          <PasSekcji wariant="cichy" tytul="Skala i zmiana" />

          <PlytaGlowna
            kartaId="SALES-01"
            etykieta="Wartość sprzedaży (ceny katalogowe)"
            wartosc={null}
            opis={
              <Typography variant="body2">
                ceny katalogowe pozycji zamówienia, przed zniżką szkolną — to nie jest kwota pobrana
              </Typography>
            }
            zrodlo={ZRODLO}
            okno={okno}
            dowod={dowodWartosci}
            dowodId="SALES-16"
          />

          {/*
            SEKCJE MAKIETY STOJĄ TAKŻE PRZY PUSTYM ŹRÓDLE.

            Ekran bez działów jest listą pudełek, po której trzeba wodzić wzrokiem — a przy
            pustce jest listą pustych pudełek, czyli wygląda na niedokończony zamiast na
            uczciwie pusty. Ramy odpowiadają na pytanie „co ten ekran pokazuje"; komunikat
            wyżej odpowiada na „dlaczego dziś nic w nich nie ma". To dwa różne pytania.

            Karty stoją tu w `Karta`, a nie w ręcznie stylowanym `Paper` z własnym `h4`:
            ta sama rama co wszędzie indziej, ta sama ranga nagłówka, zero duplikatu stylu.
          */}
          <PasSekcji wariant="cichy" tytul="Przebieg w czasie" />

          <Karta nadtytul="Kształt okna dzień po dniu" tytul="Sprzedaż dzień po dniu">
            <SzeregCzasowy
              seria={null}
              format={(v) => formatGrosze(v)}
              komunikatPusty="Źródło nie zwróciło ani jednego dnia sprzedaży w tym oknie. Szereg pojawi się, gdy będzie co rysować — pusty wykres z osią wyglądałby jak pomiar, który wypadł nisko."
            />
          </Karta>

          <PasSekcji wariant="cichy" tytul="Rytm tygodnia i granice tej liczby" />

          <Karta nadtytul="Średnia na wystąpienie dnia tygodnia" tytul="Rytm tygodnia">
            <SlupkiRankingowe
              pozycje={null}
              numeracja={false}
              format={(v) => formatGrosze(v)}
              komunikatPusty="Rytm tygodnia liczymy z serii dziennej, a serii w tym oknie nie ma. Siedem słupków o wysokości zero twierdziłoby, że w żaden dzień tygodnia nie sprzedajemy."
            />
          </Karta>

          <PasSekcji wariant="cichy" tytul="Skąd to pochodzi" />

          <Karta nadtytul="Jedna szkoła = jeden wiersz" tytul="Szkoły wg wartości sprzedaży">
            <SlupkiRankingowe
              pozycje={null}
              format={(v) => formatGrosze(v)}
              komunikatPusty="Nie ma czego szeregować: w tym oknie żadna szkoła nie ma zapisanej sprzedaży."
            />
          </Karta>
        </Stack>
      ) : null}

      {!isLoading && !error && !pokazPustke ? (
        <Stack spacing={3} className="sygnal-kaskada">
          {/*
            CZTERY DZIAŁY Z MAKIETY — `.sprz-sekcja`. Ekran jest długi i bez nazwanych działów
            czyta się jak ściana kafelków. Wariant `cichy` jest MNIEJSZY od tytułu karty
            z rozmysłem: ma porządkować przewijanie, a nie konkurować stopniem z liczbą-bohaterem.
          */}
          <PasSekcji wariant="cichy" tytul="Skala i zmiana" />

          <PlytaGlowna
            kartaId="SALES-01"
            etykieta="Wartość sprzedaży (ceny katalogowe)"
            wartosc={wartoscSprzedazy === null ? null : formatGrosze(wartoscSprzedazy)}
            opis={
              <Typography variant="body2">
                ceny katalogowe pozycji zamówienia, przed zniżką szkolną — to nie jest kwota pobrana
              </Typography>
            }
            zrodlo={ZRODLO}
            okno={okno}
            dowod={dowodWartosci}
            dowodId="SALES-16"
            boczne={
              <>
                <MiniMetryka
                  kartaId="SALES-02"
                  wartosc={zamowieniaDoPokazania === null ? null : formatCount(zamowieniaDoPokazania)}
                  etykieta="zamówień"
                />
                {/* Porcje to INNA jednostka wolumenu niż zamówienia i tego źródło nie liczy.
                    Kafelek zostaje pusty świadomie — podstawienie liczby zamówień byłoby zmyśleniem. */}
                <MiniMetryka wartosc={null} etykieta="porcji · brak w źródle" />
                <MiniMetryka
                  kartaId="SALES-04"
                  wartosc={szkolyDoPokazania === null ? null : formatCount(szkolyDoPokazania)}
                  etykieta="szkół w zasięgu"
                />
              </>
            }
          />

          <PasSekcji wariant="cichy" tytul="Przebieg w czasie" />

          {/* Seria dzienna: backend liczył ją od początku i wyrzucał, bo nie było jej w kontrakcie
              klienta. Dopisana w R5-15-006 — patrz DailyTrendChart.

              RAMA STOI TAKŻE WTEDY, GDY SERII NIE MA. Wcześniej cały blok znikał przy pustej
              serii, więc ekran z wynikiem i ekran bez serii miały RÓŻNĄ liczbę działów —
              a czytelnik nie miał jak odróżnić „nie narysowano" od „nie ma czego rysować". */}
          {data?.charts?.daily_trend?.length ? (
            <DailyTrendChart points={data.charts.daily_trend} kartaId="SALES-06" />
          ) : (
            <Karta nadtytul="Kształt okna dzień po dniu" tytul="Sprzedaż dzień po dniu">
              <SzeregCzasowy
                seria={null}
                format={(v) => formatGrosze(v)}
                komunikatPusty="Wynik jest, ale seria dzienna nie przyszła w tej odpowiedzi. To brak w zasilaniu wykresu, nie brak sprzedaży — liczba wyżej pochodzi z tego samego odczytu."
              />
            </Karta>
          )}

          <PasSekcji wariant="cichy" tytul="Rytm tygodnia i granice tej liczby" />

          {/*
            RYTM TYGODNIA — makieta ma tę sekcję, produkt jej nie miał. Nie potrzebowała ani
            jednego nowego pola w kontrakcie: dzień tygodnia jest funkcją daty punktu serii,
            którą ekran już czyta.

            Słupek niesie ŚREDNIĄ NA WYSTĄPIENIE, nie sumę. Suma faworyzowałaby dzień, który
            w oknie wypadł częściej — a to artefakt kalendarza, nie zachowanie klientów.
            Uzasadnienie i testy: `lib/rytmTygodnia.ts`.
          */}
          <Karta nadtytul="Średnia na wystąpienie dnia tygodnia" tytul="Rytm tygodnia">
            <SlupkiRankingowe
              pozycje={
                rytm
                  ? rytm.map((d) => ({ nazwa: d.dzien, wartosc: d.srednia }))
                  : null
              }
              numeracja={false}
              maks={7}
              format={(v) => formatGrosze(v)}
              komunikatPusty="Rytm tygodnia liczymy z serii dziennej, a serii w tej odpowiedzi nie ma."
            />
            {rytm ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, maxWidth: '78ch' }}>
                {rytmZrownowazony
                  ? `Każdy z ${formatCount(rytm.length)} dni tygodnia wystąpił w tym oknie tyle samo razy (${formatCount(rytm[0]!.wystapien)}×), więc słupki są wprost porównywalne.`
                  : `Dni tygodnia wystąpiły w tym oknie RÓŻNĄ liczbę razy (${rytm.map((d) => `${d.dzien}: ${d.wystapien}×`).join(', ')}). Słupek to średnia na wystąpienie, więc porównanie jest uczciwe — ale dzień o mniejszej liczbie wystąpień jest oparty na mniejszej próbie.`}{' '}
                Dzień tygodnia, którego w oknie nie było, nie ma słupka — brak dnia to nie sprzedaż zero.
              </Typography>
            ) : null}
          </Karta>

          {/* Granice tej liczby. Zamówienia i porcje mają własne dowody, ale nie własne płyty —
              na ekranie może być tylko jedna liczba-bohater, inaczej hierarchia przestaje działać. */}
          <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '68ch' }}>
              <strong className="sygnal-liczba" data-cc-card-id="SALES-05">
                {formatCount(zamowieniaDoPokazania)}
              </strong>{' '}
              zamówień to nie to samo co
              liczba porcji: jedno zamówienie bywa na kilka dań. Liczby porcji to źródło nie zwraca, więc jej tu nie ma.
            </Typography>
            <Box sx={{ ml: -1 }}>
              <PrzyciskDowodu dowod={dowodZamowien} etykieta="Dowód: zamówienia" kartaId="SALES-17" />
            </Box>
            <PrzyciskDowodu dowod={dowodPorcji} etykieta="Dowód: porcje" />
          </Stack>

          <PasSekcji wariant="cichy" tytul="Skąd to pochodzi" />

          <Karta
            nadtytul="Jedna szkoła = jeden wiersz"
            tytul="Ranking szkół"
            akcja={<PrzyciskDowodu dowod={dowodRankingu} etykieta="Skąd ten ranking" kartaId="SALES-18" />}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              data-cc-card-id="SALES-07"
              sx={{ display: 'block', mb: 2 }}
            >
              {formatCount(wiersze.length)} szkół w zasięgu analizy · {formatCount(szkolZeSprzedaza)} ze sprzedażą ·{' '}
              {formatCount(szkolBezSprzedazy)} bez sprzedaży · okno {okno} ({formatCount(dniKalendarzowe)} dni
              kalendarzowych). Kolejność malejąco po wartości katalogowej.
            </Typography>

            {/* Bez własnej ramy: tabela stoi w karcie. */}
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table
                size="small"
                aria-label="Ranking szkół wg wartości sprzedaży"
                sx={{
                  // Rytm buduje ODSTĘP, nie kreska. Siatka linii wokół każdej komórki jest głównym
                  // powodem, dla którego panele administracyjne wyglądają jak arkusz kalkulacyjny.
                  '& .MuiTableCell-root': { borderBottom: 'none', py: 'var(--o2)' },
                  '& .MuiTableCell-head': { borderBottom: '1px solid var(--linia)', pb: 'var(--o2)' },
                  '& .MuiTableBody-root .MuiTableRow-root:hover': {
                    background: 'var(--powierzchnia-2)',
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell data-cc-card-id="SALES-08">Szkoła</TableCell>
                    <TableCell data-cc-card-id="SALES-09">Miasto</TableCell>
                    {/*
                      NIE „Obrót". Ta kolumna niesie sumę cen KATALOGOWYCH, czyli wartość przed
                      zniżką szkolną — a nie pieniądze, które wpłynęły. Nazwanie tego obrotem stawia
                      liczbę w jednym rzędzie z „Sumą transakcji" starego panelu, która liczy kwoty
                      pobrane, i sugeruje różnicę w danych tam, gdzie jest różnica w definicji (ADR-001).
                    */}
                    <TableCell align="right" data-cc-card-id="SALES-10">
                      Wartość katalogowa
                    </TableCell>
                    <TableCell align="right" data-cc-card-id="SALES-11">
                      Zamówienia
                    </TableCell>
                    <TableCell align="right" data-cc-card-id="SALES-12">
                      Dni wg grafiku
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ranking.map((row) => {
                    const udzial = maksWiersza > 0 ? (row.period_sales_grosze / maksWiersza) * 100 : 0;
                    return (
                      <TableRow key={String(row.school_id)}>
                        <TableCell sx={{ fontWeight: 600 }}>{row.school_name ?? `#${row.school_id}`}</TableCell>
                        <TableCell sx={{ color: 'var(--tekst-slaby)' }}>{row.city ?? '—'}</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', justifyContent: 'flex-end' }}>
                            {/* Pasek proporcji jest DEKORACJĄ liczby stojącej obok — stąd aria-hidden.
                                Szerokość jest dokładnie proporcjonalna: żadnego minimum „żeby było
                                widać", bo to zawyżałoby wizualnie najmniejsze wiersze. */}
                            <Box
                              aria-hidden
                              sx={{
                                width: 96,
                                height: 'var(--o2)',
                                flex: 'none',
                                borderRadius: 'var(--r-pigulka)',
                                background: 'var(--tlo-glebokie)',
                                overflow: 'hidden',
                              }}
                            >
                              <Box
                                className="sygnal-slupek"
                                sx={{
                                  width: `${udzial}%`,
                                  height: '100%',
                                  borderRadius: 'var(--r-pigulka)',
                                  background: 'var(--zloto)',
                                }}
                              />
                            </Box>
                            <Box component="span" className="sygnal-liczba" sx={{ minWidth: '11ch' }}>
                              {formatGrosze(row.period_sales_grosze)}
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell align="right" className="sygnal-liczba">
                          {formatCount(row.period_orders)}
                        </TableCell>
                        <TableCell align="right" className="sygnal-liczba" sx={{ color: 'var(--tekst-slaby)' }}>
                          {formatCount(row.period_expected_days)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, maxWidth: '78ch' }}>
              „Dni wg grafiku" to dni, w których szkoła wg kalendarza i grafiku miała sprzedawać — nie dni, w których
              sprzedała. Szkoła z zerem w tej kolumnie nie miała w oknie ani jednego zaplanowanego dnia dostaw.
            </Typography>
          </Karta>
        </Stack>
      ) : null}
    </Box>
  );
}

/**
 * Stan pusty. Nazywa RODZAJ pustki, bo „brak danych" i „brak sprzedaży" to dwie różne odpowiedzi
 * i prowadzą do dwóch różnych działań: pierwsza do sprawdzenia zasilania, druga do telefonu.
 *
 * Okres podajemy ZAWSZE i w postaci „RRRR-MM-DD → RRRR-MM-DD" — brak bez podanego okna jest
 * nieweryfikowalny, więc bezwartościowy. Nie pokazujemy tu ani jednej wyzerowanej kwoty: ekran,
 * który deklaruje brak i jednocześnie rysuje 0,00 zł, sam sobie przeczy.
 */
function PustkaSprzedazy({
  rodzaj,
  okno,
  szkolWZasiegu,
  dowod,
}: {
  rodzaj: 'puste-zrodlo' | 'zmierzone-zero';
  okno: string;
  szkolWZasiegu: number | null;
  dowod: Dowod;
}) {
  const zero = rodzaj === 'zmierzone-zero';

  return (
    <Paper
      variant="outlined"
      data-cc-card-id="SALES-13"
      sx={{
        p: { xs: 'var(--o4)', md: 'var(--o6)' },
        borderStyle: 'dashed',
        background: 'var(--powierzchnia-2)',
      }}
    >
      <Typography variant="overline" sx={{ color: zero ? 'var(--uwaga)' : 'var(--krytyczny)' }}>
        {zero ? 'sygnał biznesowy' : 'sygnał techniczny'}
      </Typography>

      {/* RANGA h2, nie h4. Ten blok stoi PRZED pierwszym działem ekranu, więc `h4` po `h1`
          było przeskokiem rangi o dwa stopnie — czytnik ekranu ogłaszał brakujący poziom.
          Stopień optyczny (`variant="h6"`) zostaje bez zmian; ranga to struktura, nie wygląd. */}
      <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
        {zero ? 'Brak sprzedaży w tym oknie' : 'Brak danych o sprzedaży w tym oknie'}
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: '72ch' }}>
        {zero
          ? `Źródło odpowiedziało i policzyło: w zasięgu analizy jest ${formatCount(szkolWZasiegu)} szkół, a żadna nie ma w tym oknie ani jednego zamówienia. To jest zmierzone zero, czyli wynik — nie awaria zasilania danych.`
          : 'Źródło zwróciło pusty zbiór: zero zamówień i zero szkół. To NIE jest zmierzone zero — nie było czego sumować. Sprzedaż w tym oknie mogła być; w tym źródle jej nie ma.'}
      </Typography>

      <Stack direction="row" spacing={2} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="body2" className="sygnal-liczba" sx={{ fontWeight: 700 }}>
          Okres: {okno}
        </Typography>
        <PrzyciskDowodu dowod={dowod} />
      </Stack>
    </Paper>
  );
}
